import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import InputGroups from "@/UI/components/InputGroups";
import ConfirmPopup, { type ConfirmPopupDetails } from "@/UI/popups/ConfirmPopup";
import ChallengeTargetPopup, { type ChallengeTargetPopupDetails } from "@/UI/popups/ChallengeTargetPopup";
import ChallengeTiersPopup, { type ChallengeTiersItem, type ChallengeTiersPopupDetails } from "@/UI/popups/ChallengeTiersPopup";
import { FormControlLabel, MenuItem, Select, Switch, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface LangValue {
    lang: string;
    value: string;
    default?: boolean;
}

interface InitialChallengeData {
    _id?: string;
    name: LangValue[];
    description: LangValue[];
    trigger: "money" | "order" | "product" | "category";
    targetId: string | null;
    coffeeBeans: number;
    count: number;
    days: number | null;
    consecutive: boolean;
    startDate: string;
    endDate: string;
    minPrice: number | null;
    repeatable: boolean;
    active: boolean;
}

export interface ChallengesFormDetails {
    initialData?: InitialChallengeData;
    onSuccess: () => void;
}

interface FormState {
    nameAr: string;
    nameEn: string;
    descriptionAr: string;
    descriptionEn: string;
    trigger: "money" | "order" | "product" | "category";
    targetId: string;
    targetName: string;
    tiers: ChallengeTiersItem[];
    coffeeBeans: string;
    count: string;
    startDate: string;
    endDate: string;
    days: string;
    consecutive: boolean;
    minPrice: string;
    repeatable: boolean;
    active: boolean;
}

// ── MUI Shared Styles ─────────────────────────────────────────────────────────

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": {
            color: "var(--color-orange)",
        },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-lev3)",
        "& fieldset": {
            borderColor: "var(--color-gray-01)",
            borderWidth: "1.5px",
        },
        "& fieldset legend": {
            textAlign: dir === "rtl" ? "right" : "left",
            marginLeft: dir === "rtl" ? "auto" : undefined,
            marginRight: dir === "rtl" ? "0px" : undefined,
        },
        "&:hover fieldset": {
            borderColor: "var(--color-gray-02)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "var(--color-orange)",
            borderWidth: "1.5px",
        },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
});

const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": {
        color: "var(--color-orange)",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: "var(--color-orange)",
    },
};

const getSelectSx = (dir: "rtl" | "ltr") => ({
    ...getTextFieldSx(dir),
    "& .MuiSelect-select": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function getLangValue(arr: LangValue[] | undefined, lang: string): string {
    if (!arr || !Array.isArray(arr)) return "";
    const found = arr.find((v) => v.lang === lang);
    return found?.value || "";
}

function buildInitialForm(initial: InitialChallengeData | undefined): FormState {
    const formatDateForInput = (dateString: string | undefined): string => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
    };

    if (!initial) {
        const today = new Date().toISOString().split("T")[0];
        return {
            nameAr: "",
            nameEn: "",
            descriptionAr: "",
            descriptionEn: "",
            trigger: "money",
            targetId: "",
            targetName: "",
            tiers: [],
            coffeeBeans: "10",
            count: "1",
            startDate: today,
            endDate: today,
            days: "",
            consecutive: false,
            minPrice: "",
            repeatable: false,
            active: true,
        };
    }

    return {
        nameAr: getLangValue(initial.name, "ar"),
        nameEn: getLangValue(initial.name, "en"),
        descriptionAr: getLangValue(initial.description, "ar"),
        descriptionEn: getLangValue(initial.description, "en"),
        trigger: initial.trigger ?? "money",
        targetId: initial.targetId || "",
        targetName: "",
        tiers: [],
        coffeeBeans: String(initial.coffeeBeans ?? 10),
        count: String(initial.count ?? 1),
        startDate: formatDateForInput(initial.startDate),
        endDate: formatDateForInput(initial.endDate),
        days: initial.days ? String(initial.days) : "",
        consecutive: initial.consecutive ?? false,
        minPrice: initial.minPrice ? String(initial.minPrice) : "",
        repeatable: initial.repeatable ?? false,
        active: initial.active ?? true,
    };
}

function buildRequestData(form: FormState) {
    const name: LangValue[] = [];
    if (form.nameAr.trim()) name.push({ lang: "ar", value: form.nameAr.trim(), default: true });
    if (form.nameEn.trim()) name.push({ lang: "en", value: form.nameEn.trim() });

    const description: LangValue[] = [];
    if (form.descriptionAr.trim()) description.push({ lang: "ar", value: form.descriptionAr.trim(), default: true });
    if (form.descriptionEn.trim()) description.push({ lang: "en", value: form.descriptionEn.trim() });

    return {
        name,
        description,
        trigger: form.trigger,
        targetId: form.targetId.trim() || null,
        tiers: form.tiers.length > 0 ? form.tiers.map((tier) => tier._id) : null,
        coffeeBeans: parseInt(form.coffeeBeans) || 10,
        count: parseInt(form.count) || 1,
        startDate: form.startDate,
        endDate: form.endDate,
        days: form.days.trim() ? parseInt(form.days) : null,
        consecutive: form.consecutive,
        minPrice: form.minPrice.trim() ? parseFloat(form.minPrice) : null,
        repeatable: form.repeatable,
        active: form.active,
    };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
    onClose: () => void;
    details?: ChallengesFormDetails;
}

export default function ChallengesForm({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();

    const [form, setForm] = useState<FormState>(() => buildInitialForm(details?.initialData));

    // ── Clear target when trigger changes ────────────────────────────────────
    useEffect(() => {
        setForm((prev) => ({ ...prev, targetId: "", targetName: "" }));
    }, [form.trigger]);

    // ── Helpers ───────────────────────────────────────────────────────────────

    const showError = (message: string) => {
        const name = "challenge-form-error";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message,
                variant: "danger",
                confirmLabel: t.challengeForm.ok,
                onConfirm: () => {},
            } as ConfirmPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    // ── Target Popup ──────────────────────────────────────────────────────────

    const openTargetPopup = () => {
        const name = "challenge-target-popup";
        removeComp(name);
        addComp({
            name,
            Component: ChallengeTargetPopup,
            details: {
                type: form.trigger as "product" | "category",
                onSelect: (item) => {
                    setForm((prev) => ({ ...prev, targetId: item._id, targetName: item.name }));
                },
            } as ChallengeTargetPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Tiers Popup ───────────────────────────────────────────────────────────

    const openTiersPopup = () => {
        const name = "challenge-tiers-popup";
        removeComp(name);
        addComp({
            name,
            Component: ChallengeTiersPopup,
            details: {
                initialSelected: form.tiers,
                onSave: (tiers) => {
                    setField("tiers", tiers);
                },
            } as ChallengeTiersPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Submit fetch ──────────────────────────────────────────────────────────

    const { loading: submitting, error: submitError, status: submitStatus, invoke: submitInvoke } = useFetch("challenges", {}, false);

    const wasSubmitting = useRef(false);
    useEffect(() => {
        if (submitting) {
            wasSubmitting.current = true;
        } else if (wasSubmitting.current) {
            wasSubmitting.current = false;
            if (submitStatus === 201 || submitStatus === 200) {
                details?.onSuccess?.();
                onClose();
            } else if (submitError) {
                showError(submitError);
            }
        }
    }, [submitting]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!form.nameAr.trim() && !form.nameEn.trim()) {
            showError(t.challengeForm.nameRequired);
            return;
        }

        if (!form.trigger) {
            showError(t.challengeForm.triggerRequired);
            return;
        }

        if (!form.startDate || !form.endDate) {
            showError(t.challengeForm.dateRequired);
            return;
        }

        submitInvoke({ method: "POST", data: buildRequestData(form) });
    };

    // ── Derived ────────────────────────────────────────────────────────────────

    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSxRtl = getTextFieldSx("rtl");
    const textFieldSxLtr = getTextFieldSx("ltr");

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <form className="challenges-form" onSubmit={handleSubmit} noValidate>
            <div className="challenges-form-fields">
                {/* ── Name Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.challengeForm.nameSection}>
                    <div className="cf-row cf-row--2">
                        <TextField
                            fullWidth
                            label={`${t.challengeForm.name} (${t.challengeForm.arabic})`}
                            value={form.nameAr}
                            onChange={(e) => setField("nameAr", e.target.value)}
                            sx={textFieldSxRtl}
                        />
                        <TextField
                            fullWidth
                            label={`${t.challengeForm.name} (${t.challengeForm.english})`}
                            value={form.nameEn}
                            onChange={(e) => setField("nameEn", e.target.value)}
                            sx={textFieldSxLtr}
                        />
                    </div>
                </InputGroups>

                {/* ── Description Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.challengeForm.descriptionSection}>
                    <div className="cf-row cf-row--2">
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={`${t.challengeForm.description} (${t.challengeForm.arabic})`}
                            value={form.descriptionAr}
                            onChange={(e) => setField("descriptionAr", e.target.value)}
                            sx={textFieldSxRtl}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={`${t.challengeForm.description} (${t.challengeForm.english})`}
                            value={form.descriptionEn}
                            onChange={(e) => setField("descriptionEn", e.target.value)}
                            sx={textFieldSxLtr}
                        />
                    </div>
                </InputGroups>

                {/* ── Challenge Configuration Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.challengeForm.configSection}>
                    <div className="cf-row cf-row--3">
                        <Select
                            fullWidth
                            value={form.trigger}
                            onChange={(e) => setField("trigger", e.target.value as "money" | "order" | "product" | "category")}
                            sx={getSelectSx(dir as "rtl" | "ltr")}
                        >
                            <MenuItem value="money">{t.challengeForm.triggerMoney}</MenuItem>
                            <MenuItem value="order">{t.challengeForm.triggerOrder}</MenuItem>
                            <MenuItem value="product">{t.challengeForm.triggerProduct}</MenuItem>
                            <MenuItem value="category">{t.challengeForm.triggerCategory}</MenuItem>
                        </Select>
                        <TextField
                            fullWidth
                            type="number"
                            label={t.challengeForm.coffeeBeans}
                            value={form.coffeeBeans}
                            onChange={(e) => setField("coffeeBeans", e.target.value)}
                            slotProps={{ htmlInput: { min: 0, step: 1 } }}
                            sx={getTextFieldSx(dir as "rtl" | "ltr")}
                        />
                        <TextField
                            fullWidth
                            type="number"
                            label={t.challengeForm.count}
                            value={form.count}
                            onChange={(e) => setField("count", e.target.value)}
                            slotProps={{ htmlInput: { min: 1, step: 1 } }}
                            sx={getTextFieldSx(dir as "rtl" | "ltr")}
                        />
                    </div>

                    {/* Target Selection (Product or Category) */}
                    {(form.trigger === "product" || form.trigger === "category") && (
                        <div className="cf-row" style={{ marginTop: "10px" }}>
                            {form.targetId ? (
                                <div className="cf-target-selected">
                                    <span className="cf-target-name">{form.targetName}</span>
                                    <button type="button" className="cf-target-change" onClick={openTargetPopup}>
                                        {form.trigger === "product" ? t.challengeForm.selectProduct : t.challengeForm.selectCategory}
                                    </button>
                                </div>
                            ) : (
                                <button type="button" className="cf-target-btn" onClick={openTargetPopup}>
                                    {form.trigger === "product" ? t.challengeForm.selectProduct : t.challengeForm.selectCategory}
                                </button>
                            )}
                        </div>
                    )}

                    <p className="cf-hint">{t.challengeForm.configHint}</p>
                </InputGroups>

                {/* ── Tiers Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.challengeForm.tiersSection}>
                    <button type="button" className="cf-target-btn" onClick={openTiersPopup}>
                        {form.tiers.length > 0 ? `${t.challengeForm.selectTiers} (${form.tiers.length})` : t.challengeForm.selectTiers}
                    </button>
                    {form.tiers.length > 0 && (
                        <div className="cf-tiers-tags">
                            {form.tiers.map((tier) => (
                                <span key={tier._id} className="cf-tier-tag">
                                    {tier.name}
                                </span>
                            ))}
                        </div>
                    )}
                    <p className="cf-hint">{t.challengeForm.tiersHint}</p>
                </InputGroups>

                {/* ── Date Range Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.challengeForm.dateRange}>
                    <div className="cf-row cf-row--2">
                        <TextField
                            fullWidth
                            type="date"
                            label={t.challengeForm.startDate}
                            value={form.startDate}
                            onChange={(e) => setField("startDate", e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={getTextFieldSx(dir as "rtl" | "ltr")}
                        />
                        <TextField
                            fullWidth
                            type="date"
                            label={t.challengeForm.endDate}
                            value={form.endDate}
                            onChange={(e) => setField("endDate", e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={getTextFieldSx(dir as "rtl" | "ltr")}
                        />
                    </div>
                </InputGroups>

                {/* ── Days Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.challengeForm.days}>
                    <div className="cf-row cf-row--2">
                        <TextField
                            fullWidth
                            type="number"
                            label={t.challengeForm.days}
                            value={form.days}
                            onChange={(e) => setField("days", e.target.value)}
                            slotProps={{ htmlInput: { min: 1, step: 1 } }}
                            sx={getTextFieldSx(dir as "rtl" | "ltr")}
                        />
                        <FormControlLabel
                            control={<Switch checked={form.consecutive} onChange={(e) => setField("consecutive", e.target.checked)} sx={switchSx} />}
                            label={t.challengeForm.consecutive}
                            sx={{ "& .MuiFormControlLabel-label": { fontFamily: "Alexandria, sans-serif", fontSize: "13.5px" } }}
                        />
                    </div>
                    <p className="cf-hint">{t.challengeForm.daysHint}</p>
                </InputGroups>

                {/* ── Min Price Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.challengeForm.minPrice}>
                    <TextField
                        fullWidth
                        type="number"
                        label={t.challengeForm.minPrice}
                        value={form.minPrice}
                        onChange={(e) => setField("minPrice", e.target.value)}
                        slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                        sx={getTextFieldSx(dir as "rtl" | "ltr")}
                    />
                    <p className="cf-hint">{t.challengeForm.minPriceHint}</p>
                </InputGroups>

                {/* ── Options Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.challengeForm.optionsSection}>
                    <div className="cf-switches">
                        <FormControlLabel
                            control={<Switch checked={form.repeatable} onChange={(e) => setField("repeatable", e.target.checked)} sx={switchSx} />}
                            label={t.challengeForm.repeatable}
                            sx={{ "& .MuiFormControlLabel-label": { fontFamily: "Alexandria, sans-serif", fontSize: "13.5px" } }}
                        />
                        <FormControlLabel
                            control={<Switch checked={form.active} onChange={(e) => setField("active", e.target.checked)} sx={switchSx} />}
                            label={t.challengeForm.active}
                            sx={{ "& .MuiFormControlLabel-label": { fontFamily: "Alexandria, sans-serif", fontSize: "13.5px" } }}
                        />
                    </div>
                    <p className="cf-hint">{t.challengeForm.optionsHint}</p>
                </InputGroups>
            </div>

            {/* ── Footer ── */}
            <div className="cf-footer">
                <button type="button" className="cf-btn cf-btn--cancel" onClick={onClose} disabled={submitting}>
                    {t.challengeForm.cancel}
                </button>
                <button type="submit" className="cf-btn cf-btn--submit" disabled={submitting}>
                    {submitting ? <span className="cf-spinner" /> : t.challengeForm.create}
                </button>
            </div>
        </form>
    );
}
